import { useMutation } from '@tanstack/react-query';
import type { AxiosError, AxiosResponse } from 'axios';
import _isNil from 'lodash/isNil';
import { useNavigate } from 'react-router-dom';
import { toasts } from 'ui';
import dateFormat from 'dateformat';
import { useAuth } from '../../../components/authentication';
import { useAxios } from '../../../hooks';
import { useSendEventToGTM } from '../../../hooks/useSendEventToGTM';
import { getResponseErrorMessage, logEvent, SocialLoggerConfig } from '../../../utils/common';
import { envMap, userLoggedIn } from '../../../utils/constant';
import type { CustomAxiosError } from '../../../utils/response/types';
import { updateWidgetState } from '../../Home/components/sub-components/UpdateWidgetState';
import { axiosVidhanPrivate } from '../../../api/axios';

export type SignupRequestPayload = {
  email: string;
  password: string;
  confirmPassword: string;
  source: Record<string, string>;
  token?: string;
  timezone : string;
};

type UseSignUpProps = {
  setIsLoading: (isLoading: boolean) => void;
};

const logEventCompleted = async (type: string, newUser: boolean, userUUID: string , status : string) => {
  const apiConfig: Record<string, any> = { ...SocialLoggerConfig };
  const fid = window.localStorage.getItem('nec_fid') ?? '';
  apiConfig.payload.params.loginDetails = {
    fid,
    mode: type,
    state: 'COMPLETED',
    timeStamp: dateFormat(new Date(), 'dd/mm/yyyy HH:MM:ss'),
    path: window.location.href,
    newUser: newUser.toString(),
    userUUID,
  };
  apiConfig.payload.params.fid = fid;

  if (envMap.VITE_ENVIRONMENT === 'production') {
    await axiosVidhanPrivate.post(apiConfig.url, apiConfig.payload);
  }
};

export function useSignup({ setIsLoading }: UseSignUpProps) {
  const navigate = useNavigate();
  const { axios } = useAxios();
  const { setAuth } = useAuth();

  const { sendEventToGTM } = useSendEventToGTM();

  const { mutate: signup, isLoading } = useMutation({
    mutationFn: async (request: SignupRequestPayload) => {
      return await axios.post('/user/signup', request, {
        headers: {
          'Nected-User-IP': localStorage.getItem('Nected-User-IP') ?? '',
          'X-CSRF-Token': localStorage.getItem('X-CSRF-Token') ?? '',
        },
      });
    },
    onSuccess(response: AxiosResponse, requestPayload: SignupRequestPayload) {
      const { data } = response.data;
      const { accessToken, refreshToken, workspaceUUID, userUUID } = data;
      void logEventCompleted('email', true, userUUID , 'COMPLETED');
      sendEventToGTM({
        event: 'signup',
        status: 'success',
        email: requestPayload.email,
        provider: 'form',
        userid: userUUID,
        wsid: workspaceUUID,
        stimestamp: `nec_${new Date().getTime()}`,
      });

      sendEventToGTM({
        event: 'signup_success',
        userid: userUUID,
        wsid: workspaceUUID,
        email: requestPayload.email,
        stimestamp: `nec_${new Date().getTime()}`,
      });

      if (!_isNil(requestPayload.token)) {
        delete window.localStorage.sourceRedirect;
      }

      const state = userLoggedIn;
      setIsLoading(true);
      setAuth({ accessToken, refreshToken, workspaceUUID, userUUID });
      setTimeout(
        () => {
          updateWidgetState(state, userUUID)
            .then((res) => {})
            .catch((err) => {
              // eslint-disable-next-line no-console
              console.log(err);
            })
            .finally(() => {
              setIsLoading(false);
            });

          window.localStorage.removeItem('source');
          navigate('/questionnaire');
        },
        100,
        state,
        userUUID
      );
    },
    onError(
      error: AxiosError<CustomAxiosError>,
      requestPayload: SignupRequestPayload
    ) {
      void logEvent('ERROR', `email-api`, `${requestPayload.email}-${getResponseErrorMessage(error)}`);
      toasts.error(getResponseErrorMessage(error), 'error');

      const { code } = error.response?.data ?? { code: '' };

      sendEventToGTM({
        event: 'signup',
        status: 'error',
        reason: code,
        email: requestPayload.email,
        provider: 'form',
      });
    },
  });

  return {
    signup,
    isLoading,
  };
}
